import type { FC } from 'react';

import { addDays, isBefore, formatAmsterdamDate } from '@eneco/common/date';
import { capitalizeFirstLetter } from '@eneco/common/string';
import { Spacer } from '@eneco/custom-components/TooltipArrow';
import { useFormatter } from '@eneco/i18n';
import { IconButton, Stack, Stretch, Box, Text } from '@eneco/sparky';
import { useLocale, useMediaQuery } from '@eneco/sparky/hooks';
import { ChevronLeftIcon, ChevronRightIcon } from '@eneco/sparky/icons';
import { useContent } from '@sitecore/common';
import { DynamicPricingTariffsRendering } from '@sitecore/types/DynamicPricingTariffs';

import { useDynamicPricingTariffsOptional } from '../../../../hooks/useDynamicPricingTariffs';
import { shouldShowNext } from '../../../../utils/helpers';
import { GraphViewOption } from '../../../../utils/types';


export const DateControls: FC = () => {
  const { fields } = useContent<DynamicPricingTariffsRendering>();
  const { date: dateFormatter, format, currency } = useFormatter();
  const context = useDynamicPricingTariffsOptional();
  const locale = useLocale();
  const isDesktop = useMediaQuery('md');

  if (!context) return null;
  const { data, averagePrice, selectedView, selectedDate, selectedProduct, onNextView, onPreviousView } = context;

  if (!selectedDate) return null;

  //Determine if we can show the button for the next/previous day/week/month/year
  //There is no dynamic pricing data before 25 march 2023
  const showPrevious = isBefore(new Date(2023, 2, 25), selectedDate);
  const showNext = shouldShowNext(selectedDate, selectedView, isDesktop, data?.length);

  const dateHeading: Record<GraphViewOption, string> = {
    day: formatAmsterdamDate(selectedDate, locale),
    week: `${selectedDate.getDate()} ${
      selectedDate.getMonth() !== addDays(selectedDate, 6).getMonth() ? dateFormatter.monthLong(selectedDate) : ''
    } - ${addDays(selectedDate, 6).getDate()} ${dateFormatter.monthLong(
      addDays(selectedDate, 6),
    )}, ${selectedDate.getFullYear()}`,
    month: `${capitalizeFirstLetter(dateFormatter.monthLong(selectedDate))} ${selectedDate.getFullYear()}`,
    year: `${selectedDate.getFullYear()}`,
  };

  const capitalizedView = capitalizeFirstLetter(selectedView) as Capitalize<GraphViewOption>;

  return (
    <Box padding="4">
      <Stretch>
        <Stack direction="row" alignX="justify" alignY="center">
          {showPrevious ? (
            <IconButton label={fields?.[`previous${capitalizedView}Label`]?.value} onClick={() => onPreviousView()}>
              <ChevronLeftIcon />
            </IconButton>
          ) : (
            <Spacer />
          )}

          <Stack alignX="center" gap="2">
            <Text size="BodyL" weight="bold">
              {dateHeading[selectedView]}
            </Text>
            {averagePrice && (
              <Text size={{ initial: 'BodyXS', md: 'BodyS' }}>
                {format(fields?.graphSubtitle?.value, {
                  amount: currency.euro(averagePrice),
                  unit:
                    selectedProduct === 'electricity'
                      ? fields?.graphUnitKwhText?.value
                      : fields?.graphUnitCubicMetreText?.value,
                })}
              </Text>
            )}
          </Stack>

          {showNext ? (
            <IconButton label={fields?.[`next${capitalizedView}Label`]?.value} onClick={() => onNextView()}>
              <ChevronRightIcon />
            </IconButton>
          ) : (
            <Spacer />
          )}
        </Stack>
      </Stretch>
    </Box>
  );
};
